import { createContext, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import chartUploadRepository from "../repositories/ChartUploadRespository";
import { useBoolean } from '@chakra-ui/react'
import ROUTES from "../constants/Routes";
import { useToastr } from "@laxmimanogna/code-quick-components";
import { successToast } from "../components/successToast";
import { useRef, useEffect } from "react";
import { AuthContext } from "./AuthProvider";


export const UploadContext = createContext({});

const ChartUploadProvider = props => {
    const toast = useToastr()
    const navigate = useNavigate();
    let newCharts = []
    const [isSubmitting, isState] = useBoolean(false)
    const [uploadedCharts, setUploadedCharts] = useState([])
    const [clientUsers, setClientUsers] = useState([])
    const [clientUsersLoading, setClientUsersLoading] = useBoolean(false);
    const [isGrouped, igState] = useBoolean(false);
    const uploadedChartsRef = useRef(uploadedCharts);
    const [specialtiesProviderMap, setSpecialtiesProvidersMap] = useState([]);
    const refreshSubmitModalMetadataRef = useRef(null);
    const [isvalidatingBatchName, ivbnState] = useBoolean(false);

    useEffect(() => {
        uploadedChartsRef.current = uploadedCharts;
    }, [uploadedCharts]);

    const uploadCharts = async (files, index, clientId) => {
        const formData = new FormData()
        if (index === 0) {
           igState.off();
           refreshSubmitModalMetadataRef.current();
           bulkDeleteChartUploads(uploadedCharts.map(chart => chart.id)); 
            newCharts = files.map(f => {
                return {
                    name: f.name,
                    size: `${Math.floor(f.size / 1000)}kb`,
                    value: 0
                }
            })
        }
        formData.append('client', clientId)
        formData.append('upload_chart', files[index])
        //upload the chart and set the uploadedChart
        const uploadProgress = (data) => {
            newCharts[index] = { ...newCharts[index], value: Math.round((data.loaded / data.total) * 100) }
            setUploadedCharts([...newCharts])
        }

        if (files.length > index) {
            try {
                const response = await chartUploadRepository.uploadCharts(formData, uploadProgress)
                if (response.status === 201) {
                    newCharts[index] = {
                        ...newCharts[index],
                        id: response.data.file_obj.id,
                        preview_url: response.data.file_obj.preview_url,
                        uploaded: true,
                        cancelled: false,
                        value: 100
                    }
                    setUploadedCharts(newCharts)
                }
                const toastProps = {
                    header: 'Success!',
                    description: `Files successfully uploaded`,  
                }
                successToast(toast, toastProps)
            } catch (e) {
                newCharts[index] = {
                    ...newCharts[index],
                    uploaded: false,
                    cancelled: true,
                    value: 100
                }
                setUploadedCharts([...newCharts])
                const message = e.response?.data?.message
                toast.showError({
                    description: message,
                })
            } finally {
                uploadCharts(files, index + 1, clientId)
            }
        }
    }

    const getClientSpecialtiesProvidersMap = async (payload) => {
        try {
            const specs = await chartUploadRepository.getClientSpecialtiesProvidersMap(payload);
            let newSpecs = {...specs}
            if ('specialties' in specs) {
                newSpecs = {}
                for (const spec of specs.specialties) {
                    newSpecs[spec.id] = {
                        name: spec.name,
                        providers: [{
                            id: specs.id,
                            name: specs.name
                        }]
                    }
                }
            }
            setSpecialtiesProvidersMap(newSpecs);
        } catch (e) {
            const message = e.response?.data?.message
            toast.showError({
                description: message,
            })
        } finally {

        }
    }

    const validateBatchName = async (batchName, suggestion = false) => {
        try {
            ivbnState.on();
            let payload = {batch_name: batchName}
            if (suggestion) {
                payload["suggestion"] = true;
            }
            // return {agree: "name"};
            const response = await chartUploadRepository.validateBatchName(payload);
            return response;
        } catch(e) {
            const message = e.response?.data?.message
            toast.showError({
                description: message,
            })
            return {"error": e.detail};
        } finally {
            ivbnState.off();
        }
    }

    const submitUploadedCharts = async (clientId, fileIds = undefined, selectedSpecId = undefined, selectedProviderId = undefined, batchNotes = undefined, batchName = undefined) => {
        //submit the uploaded chart and redirect to audit list page
        //submit onlu the none deleted ones
        try {
            isState.on()
            if (!fileIds) {
                fileIds = [];
                uploadedCharts.map(f => {
                    if (!f.cancelled) {
                        fileIds.push(f.id)
                    }
                })
            }  

            await chartUploadRepository.submitUploadedCharts({
                'file_ids': fileIds, 
                'client': clientId,
                'specialty_id': selectedSpecId,
                'provider_id': selectedProviderId,
                'batch_notes': batchNotes,
                'batch_name': batchName
            })
            setUploadedCharts([])
            uploadedChartsRef.current = [];
            const toastProps = {
                header: 'Success!',
                description: `Chart successfully uploaded`,  
            }
            successToast(toast, toastProps)
            navigate(ROUTES.ASSIGN_AUDITS_V2);

        } catch (e) {
            const message = e.response?.data?.message
            toast.showError({
                description: message,
            })
        } finally {
            isState.off();

        }
    }

    const deleteChart = async (id) => {
        //delete the chart and set uploadedChart data to deleted
        try {
            chartUploadRepository.deleteChart(id)
        } catch (e) {
            const message = e.response?.data?.message
            toast.showError({
                description: message,
            })
        } finally {
            // const index = uploadedCharts.findIndex(f => f.id === id)
            // uploadedCharts[index] = { ...uploadedCharts[index], uploaded: false, cancelled: true }
            // setUploadedCharts([...uploadedCharts])
            
        }
    }

    const bulkDeleteChartUploads = async (chartUploadIds) => {
        try {
            chartUploadRepository.bulkDeleteChartUpload({'chart_upload_ids': chartUploadIds})
        } catch(e) {
            toast({
                title: 'Delete Chart',
                description: `${e.toString()}`,
                status: 'success',
                duration: 9000,
                isClosable: true,
                position: 'top-right'
            });
        } finally {
            setUploadedCharts([]);
        }
    }

    const getUsers = async () => {
        setClientUsersLoading.on();
        try {
            const response = await chartUploadRepository.getClientUsers();
            setClientUsers(response || []);
        } catch (e) {
            setClientUsers([]);
            const message = e.response?.data?.message || e.message;
            toast.showError({
                description: message,
            });
        }
        finally {
            setClientUsersLoading.off();
        }
    }

    const attachFiles = async (files, chartId, callback) => {
        isState.on()
        let newFiles = files.map(f => {
            return {
                name: f.name,
                size: `${Math.floor(f.size / 1000)}kb`,
                value: 0
            }
        })
        const uploadPromises = files.map(async (file, index) => {
            const formData = new FormData();
            formData.append('files', file);

            const uploadProgress = (data) => {
                newFiles[index] = { ...newFiles[index], value: Math.round((data.loaded / data.total) * 100) };
                callback([...newFiles]);
            };

            try {
                const response = await chartUploadRepository.attachFiles(chartId, formData, uploadProgress);
                if (response.status === 201) {
                    newFiles[index] = {
                        ...newFiles[index],
                        uploaded: true,
                        cancelled: false,
                        value: 100,
                    };
                    callback([...newFiles]);
                }
            } catch (e) {
                newFiles[index] = {
                    ...newFiles[index],
                    uploaded: false,
                    cancelled: true,
                    value: 100,
                };
                callback([...newFiles]);
                const message = e.response?.data?.error;
                toast.showError({
                    description: message,
                });
            }
        });

        await Promise.all(uploadPromises);

        const allSucceeded = newFiles.every(f => f.uploaded);
        if (allSucceeded) {
            const toastProps = {
                header: 'Success!',
                description: `Files successfully attached`,
            };
            successToast(toast, toastProps);
        } else {
            const toastProps = {
                header: 'Error!',
                description: `Some files failed to attach`,
            };
            toast.showError(toastProps)
        }
        isState.off();
    };

    const mContext = {
        isSubmitting,
        uploadedCharts,
        clientUsers,
        clientUsersLoading,
        isGrouped,
        uploadedChartsRef,
        specialtiesProviderMap,
        isvalidatingBatchName,

        uploadCharts,
        submitUploadedCharts,
        deleteChart,
        getUsers,
        setUploadedCharts,
        attachFiles,
        bulkDeleteChartUploads,
        getClientSpecialtiesProvidersMap,
        igState,
        refreshSubmitModalMetadataRef,
        validateBatchName
    };

    return (
        <UploadContext.Provider value={mContext}>
            {props.children}
        </UploadContext.Provider>
    );
};

export default ChartUploadProvider;
